import { useCallback, useEffect, useState } from "react";
import { ServiceCalendarDayEvents } from "./ServiceCalendarDayEvents";
import { ServiceCalendarListScopeToggle } from "./ServiceCalendarListScopeToggle";
import { ServiceCalendarWeekGrid } from "./ServiceCalendarWeekGrid";
import { ServiceGateBanner } from "./ServiceGateBanner";
import { ghostBtnClass, inputClass } from "./serviceShared";
import { useServiceAnchorLayerGate } from "./useServiceAnchorLayerGate";
import { useServiceCalendarPanel } from "./useServiceCalendarPanel";

export function ServiceCalendarPanel(props?: {
  embed?: boolean;
  refreshSignal?: number;
  /** Fired when a calendar day is selected (not when deselected). */
  onSelectedDayChange?: (date: string) => void;
}) {
  const embed = props?.embed ?? false;
  const gate = useServiceAnchorLayerGate();
  const [navTick, setNavTick] = useState(0);
  const effectiveTick = props?.refreshSignal ?? navTick;
  const c = useServiceCalendarPanel(effectiveTick);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleWindow, setScheduleWindow] = useState("Morning");
  const [scheduleRigId, setScheduleRigId] = useState("");
  const [scheduleTechId, setScheduleTechId] = useState("");

  useEffect(() => {
    if (c.selectedDay) setScheduleDate(c.selectedDay);
  }, [c.selectedDay]);

  useEffect(() => {
    if (!c.selectedEventId) return;
    const ev = c.schedulableEvents.find((e) => e.id === c.selectedEventId);
    if (ev) {
      setScheduleRigId(ev.assigned_rig_id ?? ev.required_rig_id ?? "");
      setScheduleTechId(ev.tech_id ?? "");
    }
  }, [c.selectedEventId, c.schedulableEvents]);

  const handleRefresh = useCallback(() => {
    gate.refresh();
    setNavTick((s) => s + 1);
    c.refreshAll();
  }, [gate, c]);

  const handleSchedule = useCallback(async () => {
    if (!c.selectedEventId || !scheduleDate) return;
    const ok = await c.scheduleEvent({
      event_id: c.selectedEventId,
      scheduled_date: scheduleDate,
      scheduled_window_label: scheduleWindow,
      tech_id: scheduleTechId || null,
      assigned_rig_id: scheduleRigId || null,
    });
    if (ok) {
      setScheduleWindow("Morning");
      c.selectEvent(c.selectedEventId);
    }
  }, [c, scheduleDate, scheduleWindow, scheduleRigId, scheduleTechId]);

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className={inputClass}
        value={c.queueFilter}
        onChange={(e) => c.setQueueFilter(e.target.value)}
        disabled={!gate.enabled || c.listBusy}
      >
        <option value="all">All queues</option>
        {c.queues.map((q) => (
          <option key={q.id} value={q.id}>
            {q.label}
          </option>
        ))}
      </select>
      <select
        className={inputClass}
        value={c.rigFilter}
        onChange={(e) => c.setRigFilter(e.target.value)}
        disabled={!gate.enabled || c.listBusy}
      >
        <option value="all">All rigs</option>
        {c.rigs.map((rig) => (
          <option key={rig.id} value={rig.id}>
            {rig.designation}
          </option>
        ))}
      </select>
      <button type="button" className={ghostBtnClass} onClick={() => c.shiftWeek(-1)} disabled={!gate.enabled}>
        ← Prev week
      </button>
      <button type="button" className={ghostBtnClass} onClick={c.goToToday} disabled={!gate.enabled}>
        Today
      </button>
      <button type="button" className={ghostBtnClass} onClick={() => c.shiftWeek(1)} disabled={!gate.enabled}>
        Next week →
      </button>
      {!embed ? (
        <button type="button" className={ghostBtnClass} onClick={handleRefresh} disabled={gate.loading || c.listBusy}>
          {c.listBusy ? "Refreshing…" : "Refresh"}
        </button>
      ) : null}
    </div>
  );

  const calendarBody = gate.enabled ? (
    <>
      <div className="text-sm text-gray-600 dark:text-zinc-400">Week of {c.weekLabel}</div>
      <ServiceCalendarWeekGrid
        weekKeys={c.weekKeys}
        rollups={c.weekRollups}
        selectedDay={c.selectedDay}
        onSelectDay={(date) => {
          const next = c.selectedDay === date ? null : date;
          c.selectDay(date);
          if (next) props?.onSelectedDayChange?.(next);
        }}
      />
      <ServiceCalendarListScopeToggle
        scope={c.listScope}
        onChange={c.setListScope}
        disabled={!gate.enabled || c.listBusy}
      />
      <ServiceCalendarDayEvents
        scope={c.listScope}
        title={c.scopeTitle}
        events={c.scopeEvents}
        selectedDay={c.selectedDay}
        queues={c.queues}
        rigs={c.rigs}
        techs={c.techs}
        schedulableEvents={c.schedulableEvents}
        selectedEventId={c.selectedEventId}
        onSelectEvent={c.selectEvent}
        scheduleDate={scheduleDate}
        scheduleWindow={scheduleWindow}
        scheduleRigId={scheduleRigId}
        scheduleTechId={scheduleTechId}
        onScheduleDateChange={setScheduleDate}
        onScheduleWindowChange={setScheduleWindow}
        onScheduleRigChange={setScheduleRigId}
        onScheduleTechChange={setScheduleTechId}
        onSchedule={() => void handleSchedule()}
        actionBusy={c.actionBusy}
      />
    </>
  ) : null;

  const errorBanner =
    gate.enabled && c.localError ? (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
        {c.localError}
      </div>
    ) : null;

  if (embed) {
    return (
      <div className="w-full text-left space-y-3">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Calendar</h3>
          {toolbar}
        </div>
        {errorBanner}
        {calendarBody}
      </div>
    );
  }

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Service calendar</h2>
        {toolbar}
      </div>

      <ServiceGateBanner gate={gate} />
      {errorBanner}
      {calendarBody}
    </div>
  );
}
